<template>
  <BaseSlideOverModal
    v-model:is-modal-open="localOpen"
    width="xl"
    @close="$emit('close')"
  >
    <template #cta>
      <div class="flex h-full gap-x-1">
        <BaseButton
          v-if="internalTab !== 'form'"
          theme-color="bg-secondary text-contrast-secondary"
          :icon="
            internalTab !== 'activitiesLog' ? 'file-alt' : 'long-arrow-left'
          "
          :title="
            internalTab !== 'activitiesLog'
              ? $t('global.activity_log')
              : $t('button.back')
          "
          @click="switchAndFetchEventActivities"
        />
        <BaseButton
          v-if="isEventDeletableFromAgenda(event)"
          :color="'danger'"
          :icon="'trash'"
          :title="$t('button.delete')"
          @click="deleteEvent"
        />

        <BaseButton
          v-if="selectedEventId && internalTab !== 'address'"
          theme-color="bg-primary text-contrast-primary"
          color="warning"
          :icon="editionMode ? 'times' : 'pen'"
          :title="editionMode ? $t('button.cancel') : $t('button.edit')"
          @click="switchEditionMode"
        />

        <BaseButton
          v-if="internalTab === 'form'"
          :title="submitButtonTitle"
          size="small"
          color="primary"
          @click="submitForm"
        />

        <BaseButton
          v-if="internalTab === 'address'"
          :title="$t('button.back')"
          icon="long-arrow-left"
          size="small"
          @click="internalTab = 'form'"
        />
      </div>
    </template>
    <template #body>
      <div
        class="-m-0.5 flex h-full w-full flex-col justify-between overflow-auto p-0.5"
      >
        <template v-if="internalTab === 'display'">
          <DisplayEventModal>
            <template
              v-if="!isInPlanning && event && event.dossier_id"
              #dossier-summary
            >
              <div
                class="mt-4 rounded-lg border shadow-sm dark:border-slate-600"
              >
                <div
                  class="flex items-center justify-between border-b px-3 py-2 dark:border-slate-600"
                >
                  <span
                    class="flex items-center gap-1.5 text-sm font-semibold text-gray-600 dark:text-gray-300"
                  >
                    <FontAwesomeIcon
                      :icon="['fal', 'folder']"
                      class="text-gray-400"
                    />
                    {{ event.dossier?.reference ?? $t('global.dossier') }}
                  </span>
                  <RouterLink
                    :to="{
                      name: 'dossier_form_edit_route',
                      params: { id: event.dossier_id }
                    }"
                    class="flex items-center gap-1 text-xs text-primary hover:underline dark:text-primary-300"
                  >
                    <FontAwesomeIcon :icon="['fal', 'external-link']" />
                    {{ $t('attributes.dossier') }}
                  </RouterLink>
                </div>
                <div class="p-3">
                  <DossierConsultContent :dossier-id="event.dossier_id" />
                </div>
              </div>
            </template>
          </DisplayEventModal>
        </template>
        <template v-else-if="internalTab === 'form'">
          <EditEventModal
            :edition-mode="editionMode"
            :new-event-dossier="newEventDossier"
            :hide-type="hideType"
            planning-mode
            @address="handleAddress"
          />
        </template>
        <template v-else-if="internalTab === 'activitiesLog'">
          <EventActivityPage :event-id="event.id" />
        </template>
      </div>
      <ConfirmModal ref="deleteEventConfirm" />
    </template>
  </BaseSlideOverModal>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { PlaceType } from '@/assets/enums'
import DisplayEventModal from '@ui/modal/event/display/DisplayEventModal'
import EditEventModal from '@ui/modal/event/edit/EditEventModal'
import EventActivityPage from '@ui/modal/event/display/EventActivityPage'
import BaseSlideOverModal from '@c/addf-package/components/modal/BaseSlideOverModal.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import DossierConsultContent from '@c/dossier/DossierConsultContent.vue'
import ConfirmModal from '@c/addf-package/components/modal/ConfirmModal.vue'
import { isEventDeletableFromAgenda } from '@ui/modal/event/agendaEventTypes'

export default {
  name: 'EventModal',
  components: {
    BaseButton,
    BaseSlideOverModal,
    EventActivityPage,
    EditEventModal,
    DisplayEventModal,
    FontAwesomeIcon,
    DossierConsultContent,
    ConfirmModal
  },
  props: {
    open: {
      type: Boolean,
      required: true
    },
    selectedEventId: {
      type: [String, Number],
      required: false,
      default: null
    },
    newEventFrom: {
      type: [String, Object],
      required: false,
      default: null
    },
    newEventTo: {
      type: [String, Object],
      required: false,
      default: null
    },
    newEventPlace: {
      type: [String, Object],
      required: false,
      default: null
    },
    newEventPlaceType: {
      type: [String, Object],
      required: false,
      default: null
    },
    fullDayEvent: {
      type: Boolean,
      required: false,
      default: false
    },
    newEventType: {
      type: String,
      required: false,
      default: null
    },
    newEventPlaceKind: {
      type: String,
      required: false,
      default: null
    },
    newEventDossier: {
      type: Object,
      required: false,
      default: null
    },
    planningMode: {
      type: Boolean,
      required: false,
      default: true
    },
    // a supprimer
    refreshFunction: {
      type: Function,
      required: false,
      default: null
    },
    forceForm: {
      type: Boolean,
      required: false,
      default: false
    },
    hideType: {
      type: Boolean,
      required: false,
      default: false
    },
    isInPlanning: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['close', 'event-updated', 'update:open', 'delete-event'],
  data() {
    return {
      internalTab: 'display',
      editionMode: false,
      PlaceType: PlaceType,
      taskModalOpened: false
    }
  },
  computed: {
    ...mapGetters({
      event: 'agenda/getEvent',
      dossier: 'dossier/getDossier',
      calendarState: 'agenda/getCalendarState',
      sites: 'metadata/getSitesMetadata',
      funeralHomes: 'metadata/getFuneralHomesMetadata'
    }),
    submitButtonTitle() {
      return this.selectedEventId
        ? this.$t('button.update_event')
        : this.$t('button.add_event')
    },
    localOpen: {
      get() {
        return this.open
      },
      set(value) {
        this.$emit('update:open', value)
      }
    }
  },
  watch: {
    open: {
      async handler(newValue) {
        if (newValue) {
          if (this.selectedEventId) {
            await this.processFetchEventByIdAction({
              event_id: this.selectedEventId
            })
            if (!this.forceForm) {
              this.editionMode = false
              this.internalTab = 'display'
            } else {
              this.editionMode = true
              this.internalTab = 'form'
            }
          } else {
            await this.processFetchNewEventAction({
              from: this.newEventFrom,
              to: this.newEventTo,
              type: this.newEventType,
              dossier: this.newEventDossier,
              full_day: this.fullDayEvent
            })
            this.editionMode = true
            this.internalTab = 'form'
            if (this.newEventPlaceKind) {
              this.event.place_model = this.newEventPlace
              this.event.place_type = this.newEventPlaceType
              this.event.place_kind = this.newEventPlaceKind
              this.event.place_id = this.newEventPlace?.id
              if (this.newEventPlaceKind === 'FUN') {
                const fh = this.funeralHomes?.find(
                  (fh) => fh.id === this.newEventPlace?.funeral_home_id
                )
                this.event.funeral_home_id = fh?.id
              } else if (this.newEventPlaceKind === 'SFU') {
                const site = this.sites?.find(
                  (site) => site.id === this.newEventPlace?.site_id
                )
                const fh = this.funeralHomes?.find(
                  (fh) => fh.id === site.funeral_home_id
                )
                this.event.funeral_home_id = fh?.id
              }
            }
          }
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    ...mapActions({
      processFetchDossierByIDAction: 'dossier/fetchDossierByID',
      processFetchNewEventAction: 'agenda/fetchNewEvent',
      processPostEventAction: 'agenda/postEvent',
      processFetchEventByIdAction: 'agenda/fetchEventById',
      processUpdateEventAction: 'agenda/updateEvent',
      processFetchAddressesByDossier: 'dossier/fetchAddressesByDossier'
    }),
    ...mapMutations({
      alterEvent: 'agenda/alterEvent'
    }),
    handleAddress() {
      if (this.event.dossier_id)
        this.processFetchAddressesByDossier({
          dossier_id: this.event.dossier_id
        })
      this.internalTab = 'address'
    },
    handleAddressSelection(payload) {
      this.event.place_type = payload.type
      this.event.place_id = payload.id
      this.event.place_model.name = payload.label
      this.internalTab = 'form'
    },
    switchAndFetchEventActivities() {
      if (this.event.id && this.internalTab !== 'activitiesLog') {
        this.internalTab = 'activitiesLog'
      } else {
        this.internalTab = 'display'
      }
    },
    isEventDeletableFromAgenda,
    async deleteEvent() {
      const confirmed = await this.$refs.deleteEventConfirm.show({
        title: this.$t('validation.confirm_delete'),
        okButton: this.$t('global.yes')
      })
      if (!confirmed) return

      this.$emit('delete-event', this.event.id)
      this.$emit('update:open', false)
    },
    submitForm() {
      if (typeof this.event?.assignedPeople === 'string')
        this.event.assignedPeople = [this.event.assignedPeople]

      if (this.selectedEventId) {
        this.processUpdateEventAction({
          event: this.event,
          successCallback: (event) => {
            this.$h.toastSuccess(this.$t('toasts.event_updated'))
            this.$emit('event-updated')
            this.$emit('close')
            if (event.type === 'REC') {
              this.processFetchDossierByIDAction(event.dossier_id)
              if (this.refreshFunction) this.refreshFunction()
            }
          }
        })
      } else {
        this.processPostEventAction({
          event: this.event,
          successCallback: (event) => {
            this.$h.toastSuccess(this.$t('toasts.event_created'))
            this.$emit('event-updated')
            this.$emit('close')
            if (event.type === 'REC') {
              this.processFetchDossierByIDAction(event.dossier_id)
              if (this.refreshFunction) this.refreshFunction()
            }
          }
        })
      }
    },
    async switchEditionMode() {
      if (this.editionMode) {
        await this.processFetchEventByIdAction({
          event_id: this.selectedEventId
        })
      }
      this.internalTab = this.editionMode ? 'display' : 'form'
      this.editionMode = !this.editionMode
    }
  }
}
</script>
